<template>
	<div :class="navSidebarClasses">
		<header class="nav-sidebar__header">
			<div class="nav-sidebar__header-logo">
				<DuoToneTeamingLetterLogo
					class-name="nav-sidebar__letter-logo"
				/>
				<FontAwesomeIcon
					class="nav-sidebar__close-icon"
					:icon="[ 'fas', 'times', ]"
					:size="'lg'"
					@click="onCloseIconClicked"
				/>
			</div>
			<div class="nav-sidebar__hr" />
		</header>
		<nav :class="mainClasses"
			ref="nav"
			@scroll="tryToggleScrollIndicator"
		>
			<TeamlessInboxNavItem
				@click="$emit( 'nav', { name: 'navigateToInbox', } )"
			/>
			<NavSidebarGroup
				v-for="navGroup in primaryNavGroups"
				:key="navGroup.key"
				class="nav-sidebar__group"
				:emits="navGroup.emits"
				:heading-class="navGroup.semanticClass"
				:is-selected="navGroup.isSelected"
				:is-sub-selected="navGroup.isSubSelected"
				:scale="navGroup.scale"
				:theme="navGroup.theme"
				@click="$emit( navGroup.emits.eventName, navGroup.emits.eventArgs )"
				v-on="bubble( `nav`, `action` )"
			>
				<template v-slot:icon v-if="navGroup.icon || navGroup.avatar">
					<FontAwesomeIcon
						v-if="navGroup.icon"
						:icon="navGroup.icon"
					/>
					<UserAvatarSpread
						v-else-if="navGroup.avatar"
						:users="[ user ]"
					/>
				</template>
				<template v-slot:text>
					{{ navGroup.text }}
				</template>
				<template v-slot:tooltip>
					{{ navGroup.tooltip }}
				</template>
				<template v-slot:items>
					<!-- top items will always be displayed; bottom items will be hidden pending further action -->
					<ShowmoreItems
						@toggle="tryToggleScrollIndicator"
					>
						<template v-slot:primary v-if="navGroup.primaryItems.length">
							<NavSidebarItem
								v-for="navItem in navGroup.primaryItems"
								:key="navItem.key"
								class="nav-sidebar__item"
								:text="navItem.text"
								:is-selected="navItem.isSelected"
								@click="$emit( navItem.emits.eventName, navItem.emits.eventArgs )"
							/>
						</template>
						<template v-slot:secondary v-if="navGroup.secondaryItems.length">
							<NavSidebarItem
								v-for="navItem in navGroup.secondaryItems"
								:key="navItem.key"
								class="nav-sidebar__item"
								:text="navItem.text"
								:is-selected="navItem.isSelected"
								@click="$emit( navItem.emits.eventName, navItem.emits.eventArgs )"
							/>
						</template>
					</ShowmoreItems>
				</template>
			</NavSidebarGroup>
		</nav>
		<footer :class="footerClasses">
			<TeamingButton2
				class="nav-sidebar__scroll-indicator"
				v-if="showScrollIndicator"
				size="TINY"
				type="TERTIARY"
				@click="onScrollIndicatorClicked"
			>
				<FontAwesomeIcon :icon="[ `fas`, `chevron-down` ]" />
			</TeamingButton2>
			<div class="nav-sidebar__hr" />
			<NavSidebarGroup
				v-for="navGroup in secondaryNavGroups"
				:key="navGroup.key"
				class="nav-sidebar__group nav-sidebar__group--secondary"
				:emits="navGroup.emits"
				:heading-class="navGroup.semanticClass"
				:is-selected="navGroup.isSelected"
				:scale="navGroup.scale"
				:theme="navGroup.theme"
				@click="$emit( navGroup.emits.eventName, navGroup.emits.eventArgs )"
				v-on="bubble( `nav`, `action` )"
			>
				<template v-slot:icon v-if="navGroup.icon || navGroup.avatar">
					<FontAwesomeIcon
						v-if="navGroup.icon"
						:icon="navGroup.icon"
					/>
					<UserAvatarSpread
						v-else-if="navGroup.avatar"
						:users="[ user ]"
					/>
				</template>
				<template v-slot:text>
					{{ navGroup.text }}
				</template>
				<template v-slot:tooltip>
					{{ navGroup.tooltip }}
				</template>
				<template v-slot:items>
					<!-- top items will always be displayed; bottom items will be hidden pending further action -->
					<ShowmoreItems
						@toggle="tryToggleScrollIndicator"
					>
						<template v-slot:primary v-if="navGroup.primaryItems.length">
							<NavSidebarItem
								v-for="navItem in navGroup.primaryItems"
								:key="navItem.key"
								class="nav-sidebar__item"
								:text="navItem.text"
								:is-selected="navItem.isSelected"
								@click="$emit( navItem.emits.eventName, navItem.emits.eventArgs )"
							/>
						</template>
						<template v-slot:secondary v-if="navGroup.secondaryItems.length">
							<NavSidebarItem
								v-for="navItem in navGroup.secondaryItems"
								:key="navItem.key"
								class="nav-sidebar__item"
								:text="navItem.text"
								:is-selected="navItem.isSelected"
								@click="$emit( navItem.emits.eventName, navItem.emits.eventArgs )"
							/>
						</template>
					</ShowmoreItems>
				</template>
			</NavSidebarGroup>
		</footer>
	</div>
</template>

<script>
import {
	NavSidebarToggleState
} from "@teaming/common-enums";

import {
	EventsMixin,
	DuoToneTeamingLetterLogo,
	TeamingButton2
} from "@teaming/common-ui";

import NavSidebarGroup from "./NavSidebarGroup";
import NavSidebarItem from "./NavSidebarItem";
import TeamlessInboxNavItem from "../../components/TeamlessInboxNavItem.vue";
import UserAvatarSpread from "@teaming/common-ui/components/UserAvatarSpread";
import ShowmoreItems from "./ShowmoreItems";
import { debounce } from "@teaming/common-util";

export default {
	name: "NavSidebar",
	mixins: [ EventsMixin, ],
	components: {
		DuoToneTeamingLetterLogo,
		NavSidebarGroup,
		NavSidebarItem,
		UserAvatarSpread,
		ShowmoreItems,
		TeamingButton2,
		TeamlessInboxNavItem,
	},
	emits: [ "nav", "action", ],
	props: {
		user: {
			type: Object,
			required: true,
		},
		primaryNavGroups: {
			type: Array,
			required: true,
		},
		secondaryNavGroups: {
			type: Array,
			required: true,
		},
		toggleState: {
			type: String,
			required: true,
		},
	},
	data() {
		return {
			showScrollIndicator: false,
			tryToggleScrollIndicator: null,
		};
	},
	computed: {
		mainClasses() {
			return {
				"nav-sidebar__main": true,
				"nav-sidebar__main--paid": !this.user.showPaymentOption,
				"nav-sidebar__main--unpaid": this.user.showPaymentOption,
			};
		},
		footerClasses() {
			return {
				"nav-sidebar__footer": true,
				"nav-sidebar__footer--scrollIndicator": this.showScrollIndicator,
				"nav-sidebar__footer--paid": !this.user.showPaymentOption,
				"nav-sidebar__footer--unpaid": this.user.showPaymentOption,
			};
		},
		navSidebarClasses() {
			return {
				"nav-sidebar": true,
				"nav-sidebar--mobile": ( this.toggleState === NavSidebarToggleState.OPEN || this.toggleState === NavSidebarToggleState.CLOSED ),
				"nav-sidebar--open": this.toggleState === NavSidebarToggleState.OPEN,
				"nav-sidebar--closed": this.toggleState === NavSidebarToggleState.CLOSED,
			};
		},
	},
	methods: {
		async toggleScrollIndicator() {
			const { nav, } = this.$refs;
			if ( !nav ) {
				return;
			}
			await this.$nextTick();
			const { clientHeight, scrollHeight, scrollTop, } = nav;
			const navIsScrollable = ( scrollHeight > clientHeight );
			const isAtBottom = ( clientHeight + scrollTop ) === scrollHeight;
			this.showScrollIndicator = navIsScrollable && !isAtBottom;
		},
		onCloseIconClicked() {
			this.$emit( "action", {
				// intentionally empty, see NavSidebarHOC.onAction()
				name: "",
			} );
		},
		onScrollIndicatorClicked() {
			const { nav, } = this.$refs;
			if ( !nav ) {
				return;
			}
			// eslint-disable-next-line no-magic-numbers
			const top = nav.scrollTop + 100;
			nav.scrollTo( { left: 0, top, behavior: "smooth", } );
		},
	},
	async mounted() {
		this.tryToggleScrollIndicator = debounce( () => {
			this.toggleScrollIndicator();
		}, 100 );
		const { screen, } = this.$foundation();
		this.screenWidth = screen.currentWidth();
		screen.on( "resize", this.tryToggleScrollIndicator );
		// give it time to finish rendering and then evaluate
		// whether the scroll helper should be displayed
		setTimeout( () => {
			this.tryToggleScrollIndicator();
			// eslint-disable-next-line no-magic-numbers
		}, 400 );
	},
	beforeUnmount() {
		const { screen, } = this.$foundation();
		this.screenWidth = screen.currentWidth();
		screen.off( "resize", this.tryToggleScrollIndicator );
		this.tryToggleScrollIndicator = null;
	},
	async updated() {
		await this.$nextTick();
		this.tryToggleScrollIndicator();
	},
};
</script>

<style lang="scss" scoped>
$navSidebarBackgroundColor: $white;
$navSidebarShadowColor: $color1-200;
$navSidebarHRColor: $color1-200;
$navSidebarCloseIconColor: $color1-500;

.nav-sidebar {
	min-height: 100%;
	width: 100%;
	position: relative;
	box-shadow: inset -0.0625rem 0 0 $navSidebarShadowColor;
	background: $navSidebarBackgroundColor;

	// initially hidden on small devices
	display: none;

	// always visible on anything larger than a phone
	@include for-size-and-larger('md') {
		display: block;
	}
}

$headerHeight: 6.5rem;
$mainTop: $headerHeight;
// NOTE: adjust main-bottom* and footer-height* TOGETHER
$mainBottom: 12rem;
$mainBottom--unpaid: $mainBottom + 4rem;
$mainBottom--paid: $mainBottom + 1.5rem;
$footerHeight: 13.5rem;
$footerHeight--unpaid: $footerHeight + 2.5rem;
$footerHeight--paid: $footerHeight;

.nav-sidebar__header {
	position: absolute;
	top: 0;
	height: $headerHeight;
	width: 100%;

	padding-top: 2rem;
	padding-left: 1.5625rem;
	padding-right: 1.5625rem;

	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: space-between;

	@include for-size('md') {
		align-items: center;
	}

	@include for-size-and-larger('lg') {
		align-items: flex-start;
	}
}

.nav-sidebar__header-logo {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: flex-start;

	width: 100%;
}

.nav-sidebar__main {
	position: absolute;
	top: $mainTop;
	bottom: $mainBottom;
	width: 100%;
	@include auto-y-scroll();

	padding: 0 1.5rem 0.5rem 1.5rem;
}

.nav-sidebar__main--unpaid {
	bottom: $mainBottom--unpaid;
}

.nav-sidebar__main--paid {
	bottom: $mainBottom--paid;
}

.nav-sidebar__main::-webkit-scrollbar {
	display: none;
}

.nav-sidebar__footer {
	position: absolute;
	bottom: 0;
	width: 100%;
	height: $footerHeight;

	padding: 2rem 1.5rem 0 1.5rem;
}

.nav-sidebar__footer--scrollIndicator {
	padding-top: 0;
}

.nav-sidebar__footer--unpaid {
	height: $footerHeight--unpaid;
}

.nav-sidebar__footer--paid {
	height: $footerHeight--paid;
}

.nav-sidebar__group {
	margin-bottom: 1rem;
	&:last-of-type {
		margin-bottom: 0;
	}
}

.nav-sidebar__letter-logo {
	display: inline-flex;
}

.nav-sidebar__hr {
	margin-top: 0;
	margin-bottom: 1rem;
	border-bottom: 0.0625rem solid $navSidebarHRColor;
	width: 100%;
}

.nav-sidebar__close-icon {
	cursor: pointer;
	color: $navSidebarCloseIconColor;

	@include for-size-and-larger('md') {
		display: none;
	}
}

@keyframes slide-in {
	from { transform: translate( -$nav-sidebar-width ); }
	to { transform:translate( 0 ); }
}

@keyframes slide-out {
	from { transform: translate( 0 ); }
	to { transform:translate( -$nav-sidebar-width ); }
}

.nav-sidebar--mobile {
	// move this outside the layout container so that it can
	// lay over the rest of the screen content
	position: fixed;
	left: 0;
	top: 0;
	bottom: 0;
	display: block;
	width: $nav-sidebar-width;

	@include for-size-and-larger('md') {
		position: initial;
		width: unset;
	}
}

.nav-sidebar--open {
	animation-name: slide-in;
	animation-duration: 0.5s;
	animation-fill-mode: forwards;

	@include for-size-and-larger('md') {
		animation: none;
	}
}

.nav-sidebar--closed {
	animation-name: slide-out;
	animation-duration: 0.5s;
	animation-fill-mode: forwards;

	@include for-size-and-larger('md') {
		animation: none;
	}
}

.nav-sidebar__scroll-indicator {
	display: none;
	margin-bottom: 0.5rem;

	@include for-size('lg') {
		display: flex;
	}
}
</style>
